import { create } from 'zustand';

/**
 * Git sync status per workspace (git sync Stage 1, card 7): what the Git page,
 * the rail dot and the status bar show. Filled by `services/git/gitSyncStatus.js`
 * (status checks) and by the sync handlers in useWorkspaces (results).
 *
 * byWorkspace[id] = {
 *   status:     the last `git_status` answer ({ hasLocalChanges, ahead, behind, … }) or null
 *   checkedAt:  when that status was read (ms)
 *   lastSyncAt: the last successful push / pull (ms) — remembered per device
 *   lastError:  message of the last failed sync, cleared by the next success
 * }
 */
const LAST_SYNC_KEY = 'apiboo.git.lastSyncAt';

function readLastSync() {
  try {
    return JSON.parse(localStorage.getItem(LAST_SYNC_KEY) || '{}') || {};
  } catch {
    return {};
  }
}

function writeLastSync(byWorkspace) {
  try {
    const out = {};
    for (const [id, s] of Object.entries(byWorkspace)) if (s.lastSyncAt) out[id] = s.lastSyncAt;
    localStorage.setItem(LAST_SYNC_KEY, JSON.stringify(out));
  } catch {
    /* best-effort */
  }
}

function initial() {
  const byWorkspace = {};
  for (const [id, at] of Object.entries(readLastSync())) byWorkspace[id] = { lastSyncAt: at };
  return byWorkspace;
}

export const useGitSyncStore = create((set) => ({
  byWorkspace: initial(),

  // Projects, boards & documents repositories (git sync Stage 2), so the rail
  // dot counts them as it counts workspaces: key → { local } (local = tracked
  // on this device only, whose waiting changes are normal work, not a dot).
  // markScope adds one when its status is read; setLinkedScopes replaces the
  // set whenever every link is known, so a disconnected scope stops counting.
  linkedScopes: {},
  markScope: (key, { local = false } = {}) =>
    set((s) => (s.linkedScopes[key]?.local === local ? s : { linkedScopes: { ...s.linkedScopes, [key]: { local } } })),
  setLinkedScopes: (links) =>
    set(() => {
      const linkedScopes = {};
      for (const [key, git] of Object.entries(links || {})) if (git) linkedScopes[key] = { local: git.provider === 'local' };
      return { linkedScopes };
    }),

  // The workspace to show when the Git page opens from a workspace's "…" menu
  // (card 8); the page selects it and clears this.
  focusId: null,
  setFocus: (id) => set({ focusId: id == null ? null : String(id) }),

  setStatus: (id, status) =>
    set((s) => ({
      byWorkspace: { ...s.byWorkspace, [id]: { ...s.byWorkspace[id], status, checkedAt: Date.now() } },
    })),

  recordSuccess: (id) =>
    set((s) => {
      const byWorkspace = { ...s.byWorkspace, [id]: { ...s.byWorkspace[id], lastSyncAt: Date.now(), lastError: null, changeCount: 0, queued: false } };
      writeLastSync(byWorkspace);
      return { byWorkspace };
    }),

  // A local edit just landed (a save, a board or document write): the dot and
  // the Git page say "Changes waiting" at once, the way an editor marks a file
  // modified on save; the real `git status` read a moment later confirms or
  // clears it (owner, 06.10.2026: "in VS Code I instantly see the modified
  // files"). Only for something whose status was already read — i.e. tracked.
  markChanged: (id) =>
    set((s) => {
      const entry = s.byWorkspace[id];
      if (!entry?.status || entry.status.hasLocalChanges) return s;
      return { byWorkspace: { ...s.byWorkspace, [id]: { ...entry, status: { ...entry.status, hasLocalChanges: true } } } };
    }),

  // How many items the Changes view found — "3 changes waiting" (set 7b).
  setChangeCount: (id, count) =>
    set((s) => ({ byWorkspace: { ...s.byWorkspace, [id]: { ...s.byWorkspace[id], changeCount: count } } })),

  // Committed here, waiting for the network (set 7d, services/git/offlineQueue.js).
  recordQueued: (id) =>
    set((s) => ({ byWorkspace: { ...s.byWorkspace, [id]: { ...s.byWorkspace[id], queued: true, lastError: null } } })),

  // A workspace that is no longer tracked (Stop tracking): its state goes,
  // including the remembered "last saved" time.
  forget: (id) =>
    set((s) => {
      const byWorkspace = { ...s.byWorkspace };
      delete byWorkspace[String(id)];
      writeLastSync(byWorkspace);
      return { byWorkspace };
    }),

  recordError: (id, message) =>
    set((s) => ({
      byWorkspace: { ...s.byWorkspace, [id]: { ...s.byWorkspace[id], lastError: message || 'Sync failed', queued: false } },
    })),
}));

export const recordGitSyncSuccess = (id) => id && useGitSyncStore.getState().recordSuccess(String(id));
export const recordGitSyncError = (id, message) => id && useGitSyncStore.getState().recordError(String(id), message);
